import distinguishedFolderId from 'owa-service/lib/factory/distinguishedFolderId';
import FindFolderResponseMessage from 'owa-service/lib/contract/FindFolderResponseMessage';
import findFolders from '../services/findFolders';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getFolderTable from '../selectors/getFolderTable';
import getMailboxLoadingState from '../selectors/getMailboxLoadingState';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getPrimaryMailFolders } from '../actions/publicActions';
import { logUsage } from 'owa-analytics';
import { orchestrator } from 'satcheljs';
import {
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_FOLDERS_TREE_TYPE,
} from '../util/folderConstants';
import { tryConsumeFindFoldersResponse } from 'owa-session-store/lib/services/sessionDataResponseStash';
import {
    setMailboxData,
    updateFolderTable,
    setRootFolderDisplayNameForMailbox,
    setLoadingStateForMailbox,
} from '../actions/internalActions';
import MailboxLoadStateEnum from '../store/schema/MailboxLoadStateEnum';
import getDefaultAnchorMailboxForHeaders from '../util/getDefaultAnchorMailboxForHeaders';

/**
 * An orchestrator that gets the primary mail folders.
 */
export default orchestrator(getPrimaryMailFolders, async () => {
    // if loading is in progress or the folders have already been loaded we return
    let loadingState = getMailboxLoadingState(PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID);
    if (
        loadingState == MailboxLoadStateEnum.Loading ||
        loadingState == MailboxLoadStateEnum.Loaded
    ) {
        return;
    }

    // sets the loading state to 'Loading' for primary folder tree
    setLoadingStateForMailbox(PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID, MailboxLoadStateEnum.Loading);

    // try finding the primary folders from session data.
    let findFoldersJsonResponse = tryConsumeFindFoldersResponse();
    let findFolderResponse: FindFolderResponseMessage = null;
    if (findFoldersJsonResponse) {
        findFolderResponse = findFoldersJsonResponse.Body.ResponseMessages
            .Items[0] as FindFolderResponseMessage;
    } else {
        // try finding the data using findFolders call
        findFolderResponse = await findFolders(
            distinguishedFolderId({ Id: PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID })
        );
    }

    if (
        !findFolderResponse ||
        !findFolderResponse.RootFolder ||
        !findFolderResponse.RootFolder.Folders ||
        !findFolderResponse.RootFolder.ParentFolder
    ) {
        setLoadingStateForMailbox(
            PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
            MailboxLoadStateEnum.ErrorLoading
        );
        return;
    }

    // updates the folder table with the reponse.
    let parentFolderId = findFolderResponse.RootFolder.ParentFolder.FolderId.Id;
    updateFolderTable(
        findFolderResponse.RootFolder,
        PRIMARY_FOLDERS_TREE_TYPE,
        getDefaultAnchorMailboxForHeaders()
    );

    // sets the primary mailbox data with rootFolder and loaded state
    setMailboxData(
        PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
        getFolderTable().get(parentFolderId),
        MailboxLoadStateEnum.Loaded
    );

    // since the name we get from server is not the once we need to display, we reset it back
    // to the display name that was part of session settings.
    setRootFolderDisplayNameForMailbox(
        PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
        getUserConfiguration().SessionSettings.UserDisplayName
    );

    // log various counts after primary mail folders have been loaded
    logDataOnFolderLoaded();
});

function logDataOnFolderLoaded() {
    const numInboxChildFolders = getFolderTable().get(folderNameToId('inbox')).childFolderIds
        .length;
    const msgRootFolderCount = getFolderTable().get(folderNameToId('msgfolderroot')).TotalCount;
    logUsage('TnS_LoadFolders', [numInboxChildFolders, msgRootFolderCount]);
}
